import { useState, useEffect } from "react";
import { logger } from "@/lib/logger";

interface UseMarkdownLoaderProps {
  filePath: string | null;
  minLoadingTime?: number; // Délai minimum en ms
}

interface UseMarkdownLoaderReturn {
  content: string | null;
  isLoading: boolean;
  error: string | null;
}

const useMarkdownLoader = ({ 
  filePath, 
  minLoadingTime = 800 
}: UseMarkdownLoaderProps): UseMarkdownLoaderReturn => {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      setError(null);
      setContent(null);

      if (!filePath) {
        setError("Page non trouvée");
        setIsLoading(false);
        return;
      }

      const startTime = Date.now();

      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }
        
        const text = await response.text();
        
        // Calculer le temps écoulé
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        // Attendre le délai minimum si nécessaire
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        setContent(text);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Erreur lors du chargement du contenu";
        setError(errorMessage);
        logger.error("Erreur de chargement markdown", err, 'useMarkdownLoader');
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [filePath, minLoadingTime]);

  return { content, isLoading, error };
};

export default useMarkdownLoader; 